import { formatToDollar, type SuretyAccountId } from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";
import { type FC } from "react";
import { calculateDelta } from "../calculateDelta";
import { FinancialPeriodBundle } from "../hooks/useFinancialsMetrics";
import { EditableProfitabilityCard } from "./EditableProfitabilityCard";
import { FinancialMetricCard } from "./FinancialMetricCard";

export type NetIncomeFYECardProps = {
	accountId: SuretyAccountId;
	recentFYE?: FinancialPeriodBundle;
	previousFYE?: FinancialPeriodBundle;
	isManual?: boolean;
	canEdit?: boolean;
};

export const NetIncomeFYECard: FC<NetIncomeFYECardProps> = ({
	accountId,
	recentFYE,
	previousFYE,
	isManual,
	canEdit = true,
}) => {
	const periodEndDate = recentFYE?.period?.periodEndDate;
	const recentYear = Option.map(periodEndDate, (date) => DateTime.fromISO(date).toFormat("yy"));
	const fyeSuffix = `(FYE${recentYear ? ` '${recentYear}` : ""})`;

	if (!recentFYE || isManual) {
		return (
			<EditableProfitabilityCard
				testId="editable-net-income-fye-card"
				label={`Profitable ${fyeSuffix}`}
				accountId={accountId}
				isProfitable={recentFYE?.profitable}
				isFye
				readOnly={!canEdit}
			/>
		);
	}

	const recentNetProfit = recentFYE.incomeStatement?.view?.netProfit.allowed;
	const previousNetProfit = previousFYE?.incomeStatement?.view?.netProfit.allowed;
	const value = formatToDollar(recentNetProfit, { abbreviate: true, accounting: true });
	const delta = Option.doWith(
		{ recentNetProfit, previousNetProfit },
		({ recentNetProfit, previousNetProfit }) => calculateDelta(recentNetProfit, previousNetProfit),
	);

	return (
		<FinancialMetricCard
			testId="net-income-fye-card"
			label={`Net Income ${fyeSuffix}`}
			value={value}
			delta={delta}
		/>
	);
};
